import { AppBar, Toolbar, Tabs, Stack, Box } from '@mui/material';
import React, { useMemo, useRef, useState } from 'react';
import Collapse from '@mui/material/Collapse';
import NavAccountMenu from './components/NavAccountMenu';
import NavHelpButton from './components/NavHelpButton';
import { matchPath } from 'react-router';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { NavRoute } from './Routes';
import { SubNavLink } from './components/SubNavLink';
import { NavTab } from './components/NavTab';
import { Ability } from '@casl/ability';
import { Actions, Subjects, User } from '@dispatch/shared/types';
import { useMsal } from '@azure/msal-react';
import { BrandIconWordMark } from './components/BrandIcon';
import { performLogout } from '@dispatch/web/auth';

export interface TopNavBarProps {
  brandName: string;
  currentUserName: string;
  routes: NavRoute[];
  permissions?: Ability<[Actions, Subjects]>;
  hideTabs?: boolean;
  user?: User;
  /** `true` when dark mode is active — drives the theme toggle's state. */
  isDark: boolean;
  /** Flips light↔dark. Wired to the single `ThemeModeProvider` owner. */
  onToggleTheme: () => void;
}

type NavRouteWithMatch = NavRoute & { match: string };

const generateNavRouteMatches = (routes: NavRoute[]): NavRouteWithMatch[] => {
  return routes.map((r) => {
    return {
      ...r,
      match: !r?.children?.length ? r.route : `${r.route}/*`,
    };
  });
};

const getFilteredByPermissionsRoutes = (
  routes: NavRoute[],
  permissions?: Ability<[Actions, Subjects]>
): NavRoute[] =>
  routes.filter((r) => (!r.permissions ? true : r.permissions(permissions)));

function useRouteMatch(patterns: readonly string[]) {
  const { pathname } = useLocation();
  for (const pattern of patterns) {
    const possibleMatch = matchPath(pattern, pathname);
    if (possibleMatch !== null) {
      return possibleMatch;
    }
  }

  return null;
}

export function TopNavBar({
  brandName,
  currentUserName,
  routes,
  permissions,
  hideTabs,
  isDark,
  onToggleTheme,
}: TopNavBarProps) {
  const navRouteMatches = generateNavRouteMatches(
    getFilteredByPermissionsRoutes(routes, permissions)
  );
  const routeMatch = useRouteMatch(navRouteMatches.map((m) => m.match));
  const currentTab = routeMatch?.pattern?.path;
  const routesToUse = useMemo(() => navRouteMatches || [], [navRouteMatches]);
  const [value, setValue] = useState(currentTab);

  React.useEffect(() => {
    setValue(currentTab);
  }, [currentTab]);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };
  const { hasChildren, parent, children } = useMemo(() => {
    const navParent = routesToUse.find((x) => x.match === value);

    const filteredChildren = getFilteredByPermissionsRoutes(
      navParent?.children || [],
      permissions
    );

    const navHasChildren = (filteredChildren?.length ?? 0) > 0;
    return {
      hasChildren: navHasChildren,
      parent: navParent,
      children: filteredChildren,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routesToUse, value]);

  const containerRef = useRef(null);

  const { instance } = useMsal();

  const logout = () => {
    void performLogout(instance);
  };

  return (
    <Box sx={{ backgroundColor: 'dark.main' }} boxShadow={2} ref={containerRef}>
      <AppBar position="static" data-testid="navbar-menu">
        <Toolbar>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            minWidth="100%"
          >
            <Stack direction="row" alignItems="center">
              <BrandIconWordMark />
            </Stack>
            {!hideTabs && (
              <Tabs
                value={value}
                onChange={handleChange}
                centered
                data-testid="navbar-tabs"
              >
                {navRouteMatches.map((r) => (
                  <NavTab
                    label={r.title}
                    value={r.match}
                    to={r.route}
                    component={RouterLink}
                    key={r.match}
                    data-testid="navbar-tab"
                  />
                ))}
              </Tabs>
            )}

            <Stack direction="row" alignItems="center" spacing={1}>
              <NavHelpButton />
              <NavAccountMenu
                name={currentUserName}
                onLogout={logout}
                isDark={isDark}
                onToggleTheme={onToggleTheme}
              />
            </Stack>
          </Stack>
        </Toolbar>
      </AppBar>
      <Collapse in={hasChildren}>
        <Box sx={{ padding: 2 }} data-testid="navbar-submenu">
          <Stack direction="row" spacing={3}>
            {children?.map((c) => (
              <SubNavLink
                to={parent?.route + c.route}
                title={c.title}
                key={c.title}
                data-testid="navbar-sublink"
              />
            ))}
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
}

export default TopNavBar;
